import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import styles from './VerbFlowView.module.scss'
import { speak } from '../speech/speak'
import voices from '../speech/voices'
import recognitionLangs from '../speech/recognitionLangs'
import languages from '../languages'
import { celebrate, popScale } from './juice'
import { tokenizeLine, tokenizeTranscript, tokenMatches, romanizationOf, cancelTts } from '../speech/flowMatch'
import ShareWithTeacher from './ShareWithTeacher'
import { splitAround } from './DrillDetails'

import CloseIcon from '@mui/icons-material/Close'
import MicIcon from '@mui/icons-material/Mic'
import MicOffIcon from '@mui/icons-material/MicOff'
import VolumeUpIcon from '@mui/icons-material/VolumeUp'
import ReplayIcon from '@mui/icons-material/Replay'
import SkipNextIcon from '@mui/icons-material/SkipNext'
import CheckIcon from '@mui/icons-material/Check'
import KeyboardIcon from '@mui/icons-material/Keyboard'
import { macStudioFetch } from '../ollama'

const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition

// ── How one item went, for the end-of-set summary ────────────────────────────
// A plain record per item, built from what happened while it was on screen. It
// is exactly what gets sent if the learner shares the set, so strings are
// clipped here: a whole set must fit comfortably in what the server keeps.
const clip = (s, n) => String(s || '').trim().slice(0, n)
const foldForCompare = (s) => deaccent(s).toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim()
const resultOf = (it, r = {}) => {
  const attempts = r.attempts || 0
  const answer = foldForCompare(it.conjugated)
  // Wrong tries only — the eventual right answer tells a teacher nothing. A try
  // counts as right when the answer appears in it as whole words, so "tu
  // fizesses" is right, "fizessess" is not, and a recogniser that hears the
  // words around the verb doesn't turn a correct answer into a "mistake".
  const wrong = (r.tries || [])
    .filter(t => t && !` ${foldForCompare(t)} `.includes(` ${answer} `))
    .map(t => clip(t, 40))
  return {
    kind: 'verb',
    verb: clip(it.verb, 40),
    meaning: clip(it.meaning, 60),
    tenseKey: it.tense,
    person: clip(it.subjectEnglish, 24),
    pronoun: clip(it.subjectNative, 24),
    answer: clip(it.conjugated, 60),
    sentence: clip(it.sentence || `${it.prefix}${it.conjugated}${it.suffix}`.replace(/\s+/g, ' '), 180),
    english: clip(it.english, 180),
    tried: Array.from(new Set(wrong)).slice(0, 4),
    attempts,
    hinted: attempts >= STEM_AFTER,
    revealed: !!r.revealed,
    skipped: !!r.skipped,
    // First time and unaided. A spoken answer can land before any attempt has
    // been counted, so both 0 and 1 qualify.
    perfect: !!r.got && !r.revealed && !r.skipped && attempts <= 1,
  }
}

// Tenses the picker offers, per language. `promptLabel` is what the model is
// told (precise, with an example form so it cannot confuse two pasts);
// `words` are the things a learner types that should tick the chip.
const GENERIC_TENSES = [
  { key: 'past', label: 'Past', promptLabel: 'simple past tense', words: [/\bpast\b/], def: false },
  { key: 'present', label: 'Present', promptLabel: 'present tense', words: [/\bpresent\b/], def: true },
  { key: 'future', label: 'Future', promptLabel: 'future tense', words: [/\bfuture\b/] },
]
// Portuguese: the whole conjugation table, grouped by mood.
const PT_TENSES = [
  { group: 'Indicativo', key: 'ind-presente', label: 'Presente', def: true, promptLabel: 'presente do indicativo (present — eu tenho, tu tens)', words: [/\bpresent[e]?\b(?!\s+(subjunctive|do\s+subjuntivo|do\s+conjuntivo))/] },
  { group: 'Indicativo', key: 'ind-imperfeito', label: 'Pretérito Imperfeito', promptLabel: 'pretérito imperfeito do indicativo (imperfect past — eu tinha, tu tinhas)', words: [/\bimperfe(ct|ito)\b(?!\s+(subjunctive|do\s+subjuntivo))/] },
  { group: 'Indicativo', key: 'ind-perfeito', label: 'Pretérito Perfeito', promptLabel: 'pretérito perfeito do indicativo (simple past — eu tive, tu tiveste)', words: [/(?:^|[^-\w])perfeito\b/, /\bpreterite\b/, /\bpast\b/] }, // not the "perfeito" inside mais-que-perfeito
  { group: 'Indicativo', key: 'ind-mqp', label: 'Pretérito Mais-que-perfeito', promptLabel: 'pretérito mais-que-perfeito simples do indicativo (pluperfect — eu tivera, tu tiveras)', words: [/mais-que-perfeito/, /\bpluperfect\b/] },
  { group: 'Indicativo', key: 'ind-futuro', label: 'Futuro do Presente', promptLabel: 'futuro do presente do indicativo (future — eu terei, tu terás)', words: [/\bfutur[eo]\b(?!\s+(subjunctive|do\s+pret|do\s+subjuntivo))/] },
  { group: 'Indicativo', key: 'ind-condicional', label: 'Futuro do Pretérito', promptLabel: 'futuro do pretérito / condicional (conditional — eu teria, tu terias)', words: [/\bconditional\b/, /\bcondicional\b/, /futuro do pret/] },
  // The example forms are deliberately BARE — no "que", no pronoun. Spelling
  // them out as "que tu tenhas" taught the model to put the trigger and the
  // pronoun inside the answer, so the sentence read "Que tu que tu vás…".
  { group: 'Subjuntivo', key: 'subj-presente', label: 'Presente', promptLabel: 'presente do subjuntivo (present subjunctive, the form that follows "que": tenha, tenhas, tenha, tenhamos, tenhais, tenham)', words: [/present\s+subjunctive/, /presente do (sub|con)juntivo/, /subjunctive\s+present/] },
  { group: 'Subjuntivo', key: 'subj-imperfeito', label: 'Pretérito Imperfeito', promptLabel: 'pretérito imperfeito do subjuntivo (imperfect subjunctive, the form that follows "se": tivesse, tivesses, tivesse, tivéssemos, tivésseis, tivessem)', words: [/imperfect\s+subjunctive/, /imperfeito do (sub|con)juntivo/, /subjunctive\s+imperfect/] },
  { group: 'Subjuntivo', key: 'subj-futuro', label: 'Futuro', promptLabel: 'futuro do subjuntivo (future subjunctive, the form that follows "quando" or "se": tiver, tiveres, tiver, tivermos, tiverdes, tiverem)', words: [/future\s+subjunctive/, /futuro do (sub|con)juntivo/, /subjunctive\s+future/] },
  { group: 'Imperativo', key: 'imp-afirmativo', label: 'Afirmativo', promptLabel: 'imperativo afirmativo (commands: tem, tenha, tenhamos, tende, tenham — there is NO "eu" form, so use only tu / você / nós / vós / vocês as subjects)', words: [/\bimperative\b(?!\s+negative)/, /\bcommands?\b/, /imperativo afirmativo/] },
  { group: 'Imperativo', key: 'imp-negativo', label: 'Negativo', promptLabel: 'imperativo negativo (negative commands, the form that follows "não": tenhas, tenha, tenhamos, tenhais, tenham — NO "eu" form)', words: [/negative\s+imperative/, /imperativo negativo/] },
  { group: 'Infinitivo', key: 'inf-pessoal', label: 'Pessoal', promptLabel: 'infinitivo pessoal (personal infinitive: ter, teres, ter, termos, terdes, terem)', words: [/personal\s+infinitive/, /infinitivo pessoal/] },
]
// English: the tenses learners actually confuse — simple against continuous,
// and the past simple against the present perfect — rather than a bare
// past / present / future, which says nothing about which "present" is meant.
const EN_TENSES = [
  { group: 'Present', key: 'en-pres-simple', label: 'Present Simple', def: true, promptLabel: 'present simple (habits and facts — I work, she works)', words: [/present\s+simple/, /simple\s+present/] },
  { group: 'Present', key: 'en-pres-cont', label: 'Present Continuous', promptLabel: 'present continuous (happening now — I am working, she is working)', words: [/present\s+(continuous|progressive)/] },
  { group: 'Present', key: 'en-pres-perfect', label: 'Present Perfect', promptLabel: 'present perfect (have / has + past participle — I have worked, she has gone)', words: [/present\s+perfect/] },
  { group: 'Past', key: 'en-past-simple', label: 'Past Simple', promptLabel: 'past simple (a finished past action — I worked, she went)', words: [/past\s+simple/, /simple\s+past/] },
  { group: 'Past', key: 'en-past-cont', label: 'Past Continuous', promptLabel: 'past continuous (was / were + -ing — I was working)', words: [/past\s+(continuous|progressive)/] },
  { group: 'Future', key: 'en-will', label: 'Future (will)', promptLabel: 'future with will (I will work)', words: [/future\s+(simple|with\s+will)/] },
  { group: 'Future', key: 'en-going-to', label: 'Future (going to)', promptLabel: 'future with going to (I am going to work)', words: [/going\s+to/] },
]
// Spanish, Italian and French: the whole table by mood, as for Portuguese
// (kept in step with the iPhone app's Tense sets). Compound tenses are the
// auxiliary and participle together, so the blank takes both.
const ES_TENSES = [
  { group: 'Indicativo', key: 'es-presente', label: 'Presente', def: true, promptLabel: 'presente de indicativo (present — yo tengo, tú tienes)', words: [/\bpresent[e]?\b(?!\s+(subjunctive|de\s+subjuntivo))/] },
  { group: 'Indicativo', key: 'es-indefinido', label: 'Pretérito Indefinido', promptLabel: 'pretérito indefinido / perfecto simple (simple past — yo tuve, tú tuviste)', words: [/indefinido/, /\bpreterite\b/, /\bpast\s+simple\b/] },
  { group: 'Indicativo', key: 'es-imperfecto', label: 'Pretérito Imperfecto', promptLabel: 'pretérito imperfecto de indicativo (imperfect past — yo tenía, tú tenías)', words: [/\bimperfe(ct|cto)\b(?!\s+(subjunctive|de\s+subjuntivo))/] },
  { group: 'Indicativo', key: 'es-perfecto', label: 'Pretérito Perfecto', promptLabel: 'pretérito perfecto compuesto (present perfect — yo he tenido, tú has tenido; the auxiliary and participle together, nothing between them)', words: [/pret[ée]rito\s+perfecto/, /present\s+perfect/] },
  { group: 'Indicativo', key: 'es-pluscuamperfecto', label: 'Pluscuamperfecto', promptLabel: 'pretérito pluscuamperfecto (pluperfect — yo había tenido, tú habías tenido; auxiliary and participle together)', words: [/pluscuamperfecto/, /\bpluperfect\b/] },
  { group: 'Indicativo', key: 'es-futuro', label: 'Futuro', promptLabel: 'futuro simple (future — yo tendré, tú tendrás)', words: [/\bfutur[eo]\b/] },
  { group: 'Indicativo', key: 'es-condicional', label: 'Condicional', promptLabel: 'condicional simple (conditional — yo tendría, tú tendrías)', words: [/condicional/, /\bconditional\b/] },
  { group: 'Subjuntivo', key: 'es-subj-presente', label: 'Presente', hard: true, promptLabel: 'presente de subjuntivo (present subjunctive, the form that follows "que": tenga, tengas, tenga, tengamos, tengáis, tengan)', words: [/present\s+subjunctive/, /presente de subjuntivo/] },
  { group: 'Subjuntivo', key: 'es-subj-imperfecto', label: 'Pretérito Imperfecto', hard: true, promptLabel: 'pretérito imperfecto de subjuntivo, -ra form (imperfect subjunctive, the form that follows "si" or "ojalá": tuviera, tuvieras, tuviera, tuviéramos, tuvierais, tuvieran)', words: [/imperfect\s+subjunctive/, /imperfecto de subjuntivo/, /subjuntivo\s+imperfecto/] },
  { group: 'Imperativo', key: 'es-imp-afirmativo', label: 'Afirmativo', hard: true, promptLabel: 'imperativo afirmativo (commands: ten, tenga, tengamos, tened, tengan — NO "yo" form)', words: [/\bimperative\b(?!\s+negative)/, /\bcommands?\b/, /imperativo afirmativo/] },
  { group: 'Imperativo', key: 'es-imp-negativo', label: 'Negativo', hard: true, promptLabel: 'imperativo negativo (negative commands, the form that follows "no": tengas, tenga, tengamos, tengáis, tengan — NO "yo" form)', words: [/negative\s+imperative/, /imperativo negativo/] },
]
const IT_TENSES = [
  { group: 'Indicativo', key: 'it-presente', label: 'Presente', def: true, promptLabel: 'presente indicativo (present — io ho, tu hai)', words: [/\bpresent[e]?\b(?!\s+(subjunctive|congiuntivo))/] },
  { group: 'Indicativo', key: 'it-passato-prossimo', label: 'Passato Prossimo', promptLabel: 'passato prossimo (perfect — io ho avuto, io sono andato; the auxiliary and participle together, nothing between them)', words: [/passato\s+prossimo/, /\bpast\b/] },
  { group: 'Indicativo', key: 'it-imperfetto', label: 'Imperfetto', promptLabel: 'imperfetto indicativo (imperfect past — io avevo, tu avevi)', words: [/\bimperfe(ct|tto)\b(?!\s+(subjunctive|congiuntivo))/] },
  { group: 'Indicativo', key: 'it-passato-remoto', label: 'Passato Remoto', promptLabel: 'passato remoto (remote past — io ebbi, tu avesti)', words: [/passato\s+remoto/] },
  { group: 'Indicativo', key: 'it-trapassato', label: 'Trapassato Prossimo', promptLabel: 'trapassato prossimo (pluperfect — io avevo avuto, io ero andato; auxiliary and participle together)', words: [/trapassato/, /\bpluperfect\b/] },
  { group: 'Indicativo', key: 'it-futuro', label: 'Futuro Semplice', promptLabel: 'futuro semplice (future — io avrò, tu avrai)', words: [/\bfutur[eo]\b/] },
  { group: 'Condizionale', key: 'it-condizionale', label: 'Presente', promptLabel: 'condizionale presente (conditional — io avrei, tu avresti)', words: [/condizionale/, /\bconditional\b/] },
  { group: 'Congiuntivo', key: 'it-cong-presente', label: 'Presente', hard: true, promptLabel: 'congiuntivo presente (present subjunctive, the form that follows "che": abbia, abbia, abbia, abbiamo, abbiate, abbiano)', words: [/present\s+subjunctive/, /congiuntivo\s+presente/] },
  { group: 'Congiuntivo', key: 'it-cong-imperfetto', label: 'Imperfetto', hard: true, promptLabel: 'congiuntivo imperfetto (imperfect subjunctive, the form that follows "se": avessi, avessi, avesse, avessimo, aveste, avessero)', words: [/imperfect\s+subjunctive/, /congiuntivo\s+imperfetto/] },
  { group: 'Imperativo', key: 'it-imperativo', label: 'Imperativo', hard: true, promptLabel: 'imperativo (commands: abbi, abbia, abbiamo, abbiate, abbiano — NO "io" form)', words: [/\bimperative\b/, /imperativo/, /\bcommands?\b/] },
]
const FR_TENSES = [
  { group: 'Indicatif', key: 'fr-present', label: 'Présent', def: true, promptLabel: "présent de l'indicatif (present — je fais, tu fais)", words: [/\bpr[ée]sent\b(?!\s+(subjunctive|du\s+subjonctif))/] },
  { group: 'Indicatif', key: 'fr-passe-compose', label: 'Passé Composé', promptLabel: "passé composé (perfect — j'ai fait, je suis allé; the auxiliary and participle together, nothing between them)", words: [/pass[ée]\s+compos[ée]/, /\bpast\b/] },
  { group: 'Indicatif', key: 'fr-imparfait', label: 'Imparfait', promptLabel: 'imparfait (imperfect past — je faisais, tu faisais)', words: [/imparfait/, /\bimperfect\b/] },
  { group: 'Indicatif', key: 'fr-plus-que-parfait', label: 'Plus-que-parfait', promptLabel: "plus-que-parfait (pluperfect — j'avais fait, j'étais allé; auxiliary and participle together)", words: [/plus-que-parfait/, /\bpluperfect\b/] },
  { group: 'Indicatif', key: 'fr-futur', label: 'Futur Simple', promptLabel: 'futur simple (future — je ferai, tu feras)', words: [/futur\s+simple/, /\bfuture\b/] },
  { group: 'Conditionnel', key: 'fr-conditionnel', label: 'Présent', promptLabel: 'conditionnel présent (conditional — je ferais, tu ferais)', words: [/conditionnel/, /\bconditional\b/] },
  { group: 'Subjonctif', key: 'fr-subjonctif', label: 'Présent', hard: true, promptLabel: 'subjonctif présent (present subjunctive, the form that follows "que": fasse, fasses, fasse, fassions, fassiez, fassent)', words: [/subjunctive/, /subjonctif/] },
  { group: 'Impératif', key: 'fr-imperatif', label: 'Impératif', hard: true, promptLabel: 'impératif (commands: fais, faisons, faites — only tu / nous / vous)', words: [/\bimperative\b/, /imp[ée]ratif/, /\bcommands?\b/] },
]
const TENSE_SETS = { por: PT_TENSES, eng: EN_TENSES, spa: ES_TENSES, ita: IT_TENSES, fra: FR_TENSES }
// Module-level arrays, so the same language always returns the same reference
// (effects can key on it).
const tensesFor = (lang) => TENSE_SETS[lang] || GENERIC_TENSES
const defaultTenseOf = (set) => (set.find(t => t.def) || set[0]).key
// Group in first-seen order; an ungrouped set is one nameless group.
const groupTenses = (set) => {
  const order = []; const map = new Map()
  set.forEach(t => { const g = t.group || ''; if (!map.has(g)) { map.set(g, []); order.push(g) } map.get(g).push(t) })
  return order.map(name => ({ name, items: map.get(name) }))
}
// The chips a typed request should tick. A subjunctive mention beats its
// indicative twin ("present subjunctive" ticks only the subjunctive) and a
// negative imperative beats the affirmative — unless the other is named too.
const tensesMentioned = (set, query) => {
  const q = String(query || '').toLowerCase()
  let found = set.filter(t => (t.words || []).some(re => re.test(q))).map(t => t.key)
  // A subjunctive drops its indicative twin — the indicative tense with the
  // same name ("Presente", "Imperfetto", "Futuro" for "Futuro do Presente").
  const byKey = (k) => set.find(t => t.key === k)
  const subj = found.map(byKey).filter(t => /^(Subj|Cong|Conj)/.test(t.group || ''))
  if (subj.length && !/indicati/.test(q)) {
    found = found.filter(k => {
      const t = byKey(k)
      if (!/^Indicati/.test(t.group || '')) return true
      return !subj.some(s => t.label === s.label || t.label.startsWith(s.label + ' do '))
    })
  }
  const neg = found.find(k => k.endsWith('imp-negativo'))
  if (neg && !/affirmative|afirmativo/.test(q)) found = found.filter(k => k !== neg.replace('negativo', 'afirmativo'))
  return found
}
const PERSONS = [
  { key: '', label: 'All persons (mixed)' },
  { key: 'I', label: 'I' },
  { key: 'you (singular)', label: 'you (singular)' },
  { key: 'he/she', label: 'he / she' },
  { key: 'we', label: 'we' },
  { key: 'you (plural)', label: 'you (plural)' },
  { key: 'they', label: 'they' },
]

const ANSWER_MODE_KEY = 'verbAnswerMode'
const readAnswerMode = () => { try { const m = localStorage.getItem(ANSWER_MODE_KEY); return m === 'write' || m === 'speak' ? m : 'speak' } catch { return 'speak' } }

// A typed answer is graded strictly: an accent may be missed, a letter may not.
// The spoken matcher tolerates an edit or two, which is right for a recogniser
// but would accept "tenho" for "tenha" — the very distinction being drilled.
const deaccent = (s) => String(s || '').normalize('NFD').replace(/\p{M}+/gu, '')
export const typedMatches = (written, expected) => !!written && !!expected && (written === expected || deaccent(written) === deaccent(expected))

const DEFAULT_COUNT = 10
const STEM_AFTER = 2    // misses before we hand over the (unchanging) stem
const HINT_AFTER = 3    // misses before the text hint + gradual ending reveal
const REVEAL_ALL = 9    // misses before the whole answer is shown

const vocabOf = (card) => {
  const t = card.translations?.[0]
  if (card.sourceLanguage && card.sourceLanguage !== 'eng' && card.source) {
    return { word: card.source, meaning: t?.target || '', lang: card.sourceLanguage }
  }
  if (t?.target && t.targetLanguage !== 'eng') {
    return { word: t.target, meaning: card.source || '', lang: t.targetLanguage }
  }
  return null
}

const parseItems = (raw) => {
  if (!raw) return []
  let text = String(raw).replace(/```json/gi, '').replace(/```/g, '').trim()
  const a = text.indexOf('{'); const b = text.lastIndexOf('}')
  if (a !== -1 && b !== -1) text = text.slice(a, b + 1)
  try { const o = JSON.parse(text); return Array.isArray(o?.items) ? o.items : [] } catch { return [] }
}

// Turn a model item into a clean challenge: verb hidden inside its sentence,
// stem/ending guaranteed to reconstruct, and the sentence split around the verb.
// The model sometimes repeats the sentence's lead-in inside the answer — asked
// for the present subjunctive it returns "que tu vás" where the sentence
// already reads "Que tu ___", so the card showed "Que tu que tu vás…". Strip
// any run of words the prefix already ends with. Pure word overlap, so it
// works in any language and never needs a list of trigger words. Never strips
// the whole answer.
const overlapNorm = (s) => String(s || '').normalize('NFC').toLocaleLowerCase().replace(/[^\p{L}\p{N}/\s]+/gu, ' ').replace(/\s+/g, ' ').trim()
export const stripPrefixOverlap = (prefix, conjugated) => {
  const pWords = overlapNorm(prefix).split(' ').filter(Boolean)
  const cRaw = String(conjugated || '').trim().split(/\s+/).filter(Boolean)
  if (!pWords.length || cRaw.length < 2) return conjugated
  const cWords = cRaw.map(overlapNorm)
  for (let n = Math.min(pWords.length, cRaw.length - 1); n > 0; n--) {
    if (pWords.slice(-n).join(' ') === cWords.slice(0, n).join(' ')) return cRaw.slice(n).join(' ')
  }
  return conjugated
}

const shapeItem = (raw, lang) => {
  let conjugated = String(raw?.conjugated || '').trim()
  if (!conjugated) return null

  // Split the sentence around the verb so we can blank only the verb.
  const sentence = String(raw?.sentence || '').trim()
  const masked = String(raw?.sentenceMasked || '').trim()
  let prefix = '', suffix = ''
  const mparts = masked.split(/_{2,}|▁+|＿+/)
  if (masked && mparts.length >= 2) {
    prefix = mparts[0]; suffix = mparts.slice(1).join(' ').trim()
    conjugated = stripPrefixOverlap(prefix, conjugated)
  } else if (sentence) {
    const i = sentence.toLowerCase().indexOf(conjugated.toLowerCase())
    if (i >= 0) { prefix = sentence.slice(0, i); suffix = sentence.slice(i + conjugated.length) }
    else { prefix = sentence + ' '; suffix = '' }
  }

  // stem + ending must reconstruct the (possibly repaired) answer exactly.
  let stem = String(raw?.stem || '').trim()
  let ending = String(raw?.ending || '').trim()
  if (!ending || (stem + ending).toLowerCase() !== conjugated.toLowerCase()) {
    if (stem && conjugated.toLowerCase().startsWith(stem.toLowerCase()) && stem.length < conjugated.length) {
      ending = conjugated.slice(stem.length)
    } else { stem = ''; ending = conjugated }
  }

  // What counts as saying it. One entry per word; a word written with a slash
  // offers a choice ("ele/ela", "vás/vais"), so either side satisfies it.
  const targetAlts = conjugated.trim().split(/\s+/).filter(Boolean)
    .map(w => w.split('/').map(a => tokenizeLine(a, lang).map(t => t.norm).join(' ')).filter(Boolean))
    .filter(alts => alts.length)
  if (!targetAlts.length) return null

  return {
    verb: String(raw?.verb || '').trim(),
    meaning: String(raw?.meaning || '').trim(),
    tense: String(raw?.tense || '').trim(),
    subjectEnglish: String(raw?.subjectEnglish || '').trim(),
    subjectNative: String(raw?.subjectNative || '').trim(),
    conjugated, stem, ending,
    prefix, suffix,
    sentence,
    english: String(raw?.english || '').trim(),
    hint: String(raw?.hint || '').trim(),
    targetAlts,
  }
}

// `lang`: pre-pick the language in standalone mode (Special Mode passes the one
// the learner already chose); the picker still lets them change it.
export default function VerbFlowView({ cards, voiceSpeed, onClose, standalone = false, lang }) {
  // Language + class context. Standalone gets a language picker; class mode
  // derives the language and feeds the class vocab to the model as focus.
  const derived = useMemo(() => {
    const v = (cards || []).map(vocabOf).filter(Boolean)
    const counts = {}
    v.forEach(x => { counts[x.lang] = (counts[x.lang] || 0) + 1 })
    const lang = Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] || 'eng'
    return { lang, vocab: v.filter(x => x.lang === lang) }
  }, [cards])

  const [manualLang, setManualLang] = useState(lang || 'rus')
  const targetLang = standalone ? manualLang : derived.lang
  const langName = languages[targetLang] || targetLang

  // ── setup ──
  const [query, setQuery] = useState('')
  const [tenses, setTenses] = useState(['present'])   // multi-select
  const [person, setPerson] = useState('')
  const [count, setCount] = useState(DEFAULT_COUNT)
  const [phase, setPhase] = useState('setup') // setup | loading | drill | error
  const [errorMsg, setErrorMsg] = useState('')
  const [regenerating, setRegenerating] = useState(false)

  // ── drill ──
  const [items, setItems] = useState([])
  const [idx, setIdx] = useState(0)
  const [setNum, setSetNum] = useState(1)
  const [hits, setHits] = useState({ forIdx: -1, states: [] })
  const [attempts, setAttempts] = useState(0)
  const [revealed, setRevealed] = useState(false)
  const [listening, setListening] = useState(false)
  const [interim, setInterim] = useState('')
  // Answer by voice or by keyboard — remembered per device, since it tends to
  // be a settled preference (and a quiet room / a busy train decides it).
  const [answerMode, setAnswerMode] = useState(readAnswerMode)
  const [typed, setTyped] = useState('')
  const typedRef = useRef(null)
  const [micError, setMicError] = useState(null)
  const [showRoman, setShowRoman] = useState(true)
  const [combo, setCombo] = useState(0)
  const [best, setBest] = useState(0)
  // The end of a set: how the set just finished went, shown before the next one.
  const [summary, setSummary] = useState(null)
  const resultsRef = useRef([])    // how each item of the current set went, by index
  const triesRef = useRef([])      // what the learner typed or said for this item
  const revealedRef = useRef(false)
  useEffect(() => { revealedRef.current = revealed }, [revealed])

  const item = items[idx]

  const recRef = useRef(null)
  const listeningRef = useRef(false)
  const completedRef = useRef(false)
  const sessionRef = useRef(0)
  const deadSessionsRef = useRef(0)
  const advanceTimerRef = useRef(null)
  const attemptsRef = useRef(0)
  const lastFinalRef = useRef('')
  const itemRef = useRef(null)
  const comboRef = useRef(null)
  useEffect(() => { itemRef.current = item }, [item])
  useEffect(() => { if (combo > 0) popScale(comboRef.current) }, [combo])


  const toggleTense = (k) => setTenses(prev => prev.includes(k) ? (prev.length > 1 ? prev.filter(x => x !== k) : prev) : [...prev, k])

  // The tenses on offer depend on the language (Portuguese gets its whole
  // table). When the language changes, keep whatever still applies, else fall
  // back to that language's default.
  const tenseSet = tensesFor(targetLang)
  useEffect(() => {
    setTenses(prev => {
      const keys = new Set(tenseSet.map(t => t.key))
      const kept = prev.filter(k => keys.has(k))
      return kept.length ? kept : [defaultTenseOf(tenseSet)]
    })
  }, [tenseSet])

  // Keep the tense chips in sync with tense words typed in the prompt, so the
  // query and the chips can never disagree ("…past and present" ticks both).
  // A subjunctive mention beats its indicative twin ("present subjunctive"
  // ticks only the subjunctive), unless the indicative is named as well.
  useEffect(() => {
    const found = tensesMentioned(tenseSet, query)
    if (found.length) setTenses(found)
  }, [query, tenseSet])

  const canStart = tenses.length > 0 && (standalone ? query.trim().length > 0 : true)

  // ── generation ──────────────────────────────────────────────────────────
  const generate = useCallback(async (isRegen = false) => {
    if (isRegen) setRegenerating(true); else setPhase('loading')
    setErrorMsg('')

    const tenseLabels = tenses.map(k => tenseSet.find(t => t.key === k)?.promptLabel).filter(Boolean)
    // One model for every language and tense: gemma4:26b is stronger on
    // grammar than the 14B/27B pair it replaced (which split the work by
    // difficulty) and, as a mixture of experts, faster than either.
    const model = 'gemma4:26b'
    const classVocab = (!standalone && derived.vocab.length)
      ? derived.vocab.slice(0, 40).map(x => `- ${x.word} = ${x.meaning}`).join('\n')
      : ''
    const personRule = person
      ? `Every item uses the subject: ${person}.`
      : `Vary the subject across the set (I, you (singular), he/she, we, you (plural), they).`

    const prompt = `You are a ${langName} teacher creating a SPOKEN verb-conjugation drill.
${query.trim() ? `The learner asked for: "${query.trim()}". Honour this request.` : ''}
${classVocab ? `Prefer verbs the learner is currently studying (pull the verbs out of these):\n${classVocab}` : ''}

Create ${count} drill items. Spread them across these tenses: ${tenseLabels.join(', ')} (roughly even). ${personRule}
Each item drills ONE verb, conjugated for a subject in one of those tenses, inside a short natural sentence.

For each item provide:
- verb: dictionary/infinitive form of the verb
- meaning: short English meaning of the verb (e.g. "to want")
- tense: which tense this item uses — one of ${tenses.join(', ')}
- subjectEnglish: one of "I","you (singular)","he/she","we","you (plural)","they"
- subjectNative: the ${langName} subject pronoun for that person
- conjugated: ONLY the verb form itself. No pronoun, and no trigger word such as "que", "se", "quando", "não", "por", "si", "che" or "no" — even when the tense needs one. A compound tense is the auxiliary and participle together ("he tenido", "ho avuto", "j'ai fait"), with nothing between them in the sentence. Those belong in "sentence" and must NEVER appear here. For "Que tu ___ para a escola" the answer is "vás", not "que tu vás".
- stem: the unchanging leading part of "conjugated" (may be empty string)
- ending: the remaining part, so that stem + ending EXACTLY equals conjugated
- sentence: a short natural ${langName} sentence (4-8 words) using that exact conjugated verb, and carrying any trigger word the tense needs ("que", "se", "quando", "não") itself
- sentenceMasked: the SAME sentence but with the conjugated verb replaced by exactly three underscores ___
- english: the English translation of the sentence
- hint: ONE short English hint about how to form the ending — do NOT state the answer

Return ONLY minified JSON, no markdown:
{"items":[{"verb":"","meaning":"","tense":"","subjectEnglish":"","subjectNative":"","conjugated":"","stem":"","ending":"","sentence":"","sentenceMasked":"","english":"","hint":""}]}`

    try {
      const res = await macStudioFetch('generate', JSON.stringify({ model, prompt, stream: false, keep_alive: '30m', format: 'json', options: { temperature: 0.6, num_predict: 2200 } }))
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      const built = parseItems(data.response).map(r => shapeItem(r, targetLang)).filter(Boolean)
      if (built.length === 0) { setErrorMsg('The model returned no usable items — try again or reword the prompt.'); setPhase('error'); setRegenerating(false); return }
      setItems(built)
      setIdx(0)
      setSetNum(n => (isRegen ? n + 1 : 1))
      setRegenerating(false)
      setPhase('drill')
    } catch (e) {
      setErrorMsg((e.message || '').match(/Failed to fetch|Load failed/) ? 'Connection to the Mac Studio failed — check the tunnel / Ollama.' : (e.message || 'Generation failed.'))
      setPhase('error'); setRegenerating(false)
    }
  }, [standalone, derived.vocab, person, tenses, tenseSet, count, query, langName, targetLang])

  // Reset per item.
  useEffect(() => {
    if (phase !== 'drill' || !item) return
    setHits({ forIdx: idx, states: item.targetAlts.map(() => false) })
    setAttempts(0); setRevealed(false); setInterim(''); setTyped('')
    completedRef.current = false; attemptsRef.current = 0; lastFinalRef.current = ''
    triesRef.current = []; revealedRef.current = false
  }, [idx, phase, item])

  // A new set starts with a clean record.
  useEffect(() => { resultsRef.current = [] }, [items])

  const states = hits.forIdx === idx ? hits.states : []

  // The set is over: freeze how it went and show it, rather than rolling straight
  // into the next set. An item that was never recorded (it shouldn't happen)
  // counts as skipped instead of silently vanishing from the summary.
  const finishSet = useCallback(() => {
    const labelOf = (k) => {
      const t = tenseSet.find(x => x.key === k)
      return t ? (t.group ? `${t.group} · ${t.label}` : t.label) : (k || '')
    }
    const results = items.map((it, i) => {
      const r = resultsRef.current[i] || resultOf(it, { skipped: true })
      return { ...r, tense: labelOf(r.tenseKey) }
    })
    setSummary({ setNum, results })
    setPhase('summary')
  }, [items, setNum, tenseSet])

  // No side effects inside a state updater: this used to start the next set from
  // within setIdx's callback, which React is allowed to call twice.
  const advance = useCallback(() => {
    clearTimeout(advanceTimerRef.current)
    if (idx + 1 >= items.length) { finishSet(); return }
    setIdx(idx + 1)
  }, [idx, items.length, finishSet])

  const skip = () => {
    if (item && !resultsRef.current[idx]) {
      resultsRef.current[idx] = resultOf(item, { skipped: true, attempts: attemptsRef.current, revealed: revealedRef.current, tries: triesRef.current })
    }
    setCombo(0); advance()
  }

  // Verb recalled → combo, celebrate, fast next.
  useEffect(() => {
    if (hits.forIdx !== idx || !item) return
    if (item.targetAlts.length === 0 || hits.states.length !== item.targetAlts.length) return
    if (!hits.states.every(Boolean) || completedRef.current) return
    completedRef.current = true
    resultsRef.current[idx] = resultOf(item, { got: true, attempts: attemptsRef.current, revealed: revealedRef.current, tries: triesRef.current })
    celebrate(window.innerWidth / 2, window.innerHeight / 2.4, { count: 26, spread: 160 })
    setCombo(c => {
      const nc = c + 1
      setBest(b => Math.max(b, nc))
      if (nc % 5 === 0) celebrate(window.innerWidth / 2, window.innerHeight / 2.4, { count: 60, spread: 260 })
      return nc
    })
    advanceTimerRef.current = setTimeout(advance, 850)
  }, [hits, idx, item, advance])

  const applyTranscript = useCallback((transcript, isFinal) => {
    const cur = itemRef.current
    if (!cur) return
    const tokens = tokenizeTranscript(transcript, targetLang)
    setHits(prev => {
      if (prev.forIdx !== idx) return prev
      const next = [...prev.states]
      for (let ti = 0; ti < cur.targetAlts.length; ti++) {
        if (next[ti]) continue
        // Any of that position's accepted forms counts — "ele/ela" is satisfied
        // by either one, not by both.
        if (tokens.some(tok => cur.targetAlts[ti].some(alt => tokenMatches(tok, alt, targetLang)))) next[ti] = true
      }
      return { forIdx: prev.forIdx, states: next }
    })
    if (isFinal && !completedRef.current) {
      const heard = transcript.trim()
      if (heard && heard !== lastFinalRef.current) {
        lastFinalRef.current = heard
        // Kept for the summary: what was actually said, so a slip can be seen,
        // not just counted. Only the tail — the recogniser accumulates the session.
        if (triesRef.current.length < 8) triesRef.current.push(heard.slice(-60))
        attemptsRef.current += 1
        setAttempts(attemptsRef.current)
        if (attemptsRef.current >= REVEAL_ALL) setRevealed(true)
      }
    }
  }, [idx, targetLang])

  // A written answer goes through the same hit states as a spoken one, so the
  // combo, the reveal ladder and the auto-advance all behave identically.
  const submitTyped = useCallback(() => {
    const text = typed.trim()
    const cur = itemRef.current
    if (!text || !cur) return
    const words = tokenizeLine(text, targetLang).map(t => t.norm)
    setHits(prev => {
      if (prev.forIdx !== idx) return prev
      const next = [...prev.states]
      for (let ti = 0; ti < cur.targetAlts.length; ti++) {
        if (next[ti]) continue
        if (words.some(w => cur.targetAlts[ti].some(alt => typedMatches(w, alt)))) next[ti] = true
      }
      return { forIdx: prev.forIdx, states: next }
    })
    if (triesRef.current.length < 8) triesRef.current.push(text)
    attemptsRef.current += 1
    setAttempts(attemptsRef.current)
    if (attemptsRef.current >= REVEAL_ALL) setRevealed(true)
  }, [typed, targetLang, idx])

  // ── recognizer ────────────────────────────────────────────────────────────
  const stopListening = useCallback(() => {
    listeningRef.current = false; setListening(false); sessionRef.current++
    try { recRef.current?.stop() } catch { /* stopped */ }
  }, [])

  const startListening = useCallback(() => {
    if (!SpeechRecognition) { setMicError('Speech recognition is not supported in this browser.'); return }
    if (!itemRef.current) return
    setMicError(null); cancelTts()
    const session = ++sessionRef.current
    const rec = new SpeechRecognition()
    rec.lang = recognitionLangs[targetLang] || 'en-US'
    rec.continuous = true; rec.interimResults = true
    let heardSound = false
    const stopWithError = (msg) => { listeningRef.current = false; setListening(false); setMicError(msg) }
    rec.onsoundstart = () => { if (session === sessionRef.current) { heardSound = true; deadSessionsRef.current = 0 } }
    rec.onspeechstart = () => { if (session === sessionRef.current) { heardSound = true; deadSessionsRef.current = 0 } }
    rec.onresult = (event) => {
      if (session !== sessionRef.current) return
      heardSound = true; deadSessionsRef.current = 0
      let transcript = '', sawFinal = false
      for (let i = 0; i < event.results.length; i++) { transcript += event.results[i][0].transcript + ' '; if (event.results[i].isFinal) sawFinal = true }
      setInterim(transcript.trim().slice(-70))
      applyTranscript(transcript, sawFinal)
    }
    rec.onerror = (e) => {
      if (session !== sessionRef.current) return
      if (e.error === 'not-allowed' || e.error === 'service-not-allowed') stopWithError('Microphone access was blocked — allow it in browser settings.')
      else if (e.error === 'audio-capture') stopWithError('No audio device captured — pick the right microphone in browser settings.')
      else if (e.error === 'language-not-supported') stopWithError(`This browser can't recognize ${rec.lang} speech.`)
    }
    rec.onend = () => {
      if (session !== sessionRef.current) return
      if (!listeningRef.current || completedRef.current) return
      if (!heardSound) { deadSessionsRef.current++; if (deadSessionsRef.current >= 4) { stopWithError('Mic is open but no sound is reaching it — check the input in the address-bar site settings.'); return } }
      setTimeout(() => { if (session === sessionRef.current && listeningRef.current) { try { rec.start() } catch { /* raced */ } } }, 500)
    }
    recRef.current = rec; listeningRef.current = true; deadSessionsRef.current = 0; setListening(true)
    try { rec.start() } catch { setTimeout(() => { if (session === sessionRef.current && listeningRef.current) { try { rec.start() } catch { /* give up */ } } }, 400) }
  }, [targetLang, applyTranscript])

  useEffect(() => {
    if (!listeningRef.current) return
    sessionRef.current++
    try { recRef.current?.abort() } catch { /* fine */ }
    const t = setTimeout(() => { if (listeningRef.current) startListening() }, 300)
    return () => clearTimeout(t)
  }, [idx]) // eslint-disable-line

  useEffect(() => () => {
    clearTimeout(advanceTimerRef.current)
    listeningRef.current = false; sessionRef.current++
    try { recRef.current?.abort() } catch { /* fine */ }
  }, [])

  // Switching to the keyboard closes the mic; nobody wants it listening while
  // they type, and it would keep scoring the room.
  useEffect(() => {
    if (answerMode !== 'write') return
    stopListening(); setInterim(''); setMicError('')
  }, [answerMode, stopListening])

  const chooseAnswerMode = (m) => {
    setAnswerMode(m)
    try { localStorage.setItem(ANSWER_MODE_KEY, m) } catch { /* private mode */ }
  }

  const retry = () => {
    clearTimeout(advanceTimerRef.current); completedRef.current = false
    setHits({ forIdx: idx, states: (item?.targetAlts || []).map(() => false) }); setInterim(''); setTyped('')
    if (answerMode === 'write') { typedRef.current?.focus(); return }
    if (listeningRef.current) { sessionRef.current++; try { recRef.current?.abort() } catch { /* fine */ }; setTimeout(startListening, 250) }
  }
  const speakAnswer = () => { if (item) speak(item.sentence || item.conjugated, voices[targetLang] || voices['eng'], voiceSpeed) }
  const speakConjugated = () => { if (item) speak(item.conjugated, voices[targetLang] || voices['eng'], voiceSpeed) }

  // The mic has no business listening over a results screen.
  useEffect(() => { if (phase === 'summary') stopListening() }, [phase, stopListening])

  // ── render: end of set ──────────────────────────────────────────────────────
  // How the set went, what needed help, and — the learner's choice — sending it
  // to the teacher who teaches this language. Then on to the next set.
  if (phase === 'summary' && summary) {
    const rows = summary.results
    const firstTime = rows.filter(r => r.perfect).length
    const tricky = rows.filter(r => !r.perfect)
    const tensesInSet = Array.from(new Set(rows.map(r => r.tense).filter(Boolean)))
    return (
      <div className={styles.view}>
        <button className={styles.closeBtn} onClick={() => setPhase('setup')} title="Back to the drill settings"><CloseIcon /></button>
        {regenerating && <div className={styles.regenOverlay}><div className={styles.spinner} /><div className={styles.loadingText}>New set…</div></div>}

        <div className={styles.summary}>
          <div className={styles.drillTop}>
            <span className={styles.modeLabel}>Set {summary.setNum} done · {langName}</span>
            <div className={styles.comboWrap}><span className={styles.comboFire}>🏆</span><span className={styles.combo}>{best}</span></div>
          </div>

          <div className={styles.sumScore}>
            <span className={styles.sumBig}>{firstTime}<span className={styles.sumOf}> / {rows.length}</span></span>
            <span className={styles.sumCaption}>first time, no help</span>
          </div>

          {tricky.length > 0 ? (
            <>
              <div className={styles.fieldLabel}>Worth another look</div>
              <div className={styles.sumList}>
                {tricky.map((r, i) => {
                  const [before, hit, after] = splitAround(r.sentence, r.answer)
                  return (
                    <div key={i} className={styles.sumRow}>
                      <span className={styles.sumMark} title={r.skipped ? 'skipped' : r.revealed ? 'answer revealed' : 'needed more than one try'}>
                        {r.skipped ? '↷' : r.revealed ? '👁' : '↻'}
                      </span>
                      <div className={styles.sumBody}>
                        <div className={styles.sumSentence}>{before}<span className={styles.sumAnswer}>{hit}</span>{after}</div>
                        <div className={styles.sumMeta}>
                          <strong>{r.verb}</strong>{r.meaning ? ` (${r.meaning})` : ''} · {r.tense} · {r.person}{r.pronoun ? ` — ${r.pronoun}` : ''} → <strong>{r.answer}</strong>
                        </div>
                        {r.tried.length > 0 && <div className={styles.sumTried}>you tried: {r.tried.join(', ')}</div>}
                      </div>
                    </div>
                  )
                })}
              </div>
            </>
          ) : (
            <div className={styles.sumClean}>Every verb first time — clean sweep.</div>
          )}

          <ShareWithTeacher
            title={`Verb Lab · ${tensesInSet.join(', ') || 'conjugation'}`}
            langCode={targetLang}
            mode="verb-lab"
            total={rows.length}
            perfect={firstTime}
            buildDetails={() => rows}
          />

          <div className={styles.sumActions}>
            <button className={styles.generateBtn} onClick={() => generate(true)} disabled={regenerating}>Next set →</button>
            <button className={styles.linkBtn} onClick={() => setPhase('setup')}>change what you’re drilling</button>
          </div>
        </div>
      </div>
    )
  }

  // ── render: setup ──────────────────────────────────────────────────────────
  if (phase !== 'drill') {
    return (
      <div className={styles.view}>
        <button className={styles.closeBtn} onClick={onClose}><CloseIcon /></button>
        <div className={styles.panel}>
          <div className={styles.modeLabel}>Verb Lab{standalone ? '' : ` · ${langName}`}</div>
          <h1 className={styles.title}>Conjugation practice</h1>
          <p className={styles.sub}>
            {standalone ? 'Say what you want to drill — the AI builds it.' : `${derived.vocab.length} items from this class · say the conjugated verb out loud.`}
          </p>

          {phase === 'loading' ? (
            <div className={styles.loading}><div className={styles.spinner} /><div className={styles.loadingText}>Building {count} conjugation challenges…</div></div>
          ) : (
            <>
              {standalone && (
                <div className={styles.field}>
                  <span className={styles.fieldLabel}>Language</span>
                  <select className={styles.select} value={manualLang} onChange={e => setManualLang(e.target.value)}>
                    {/* English is hidden (it's the native language here) — unless it's the one chosen, as Special Mode's English does. */}
                    {Object.entries(languages).filter(([c]) => c !== 'eng' || c === manualLang).map(([c, n]) => <option key={c} value={c}>{n}</option>)}
                  </select>
                </div>
              )}

              <div className={styles.field}>
                <span className={styles.fieldLabel}>What do you want to practise?{standalone ? '' : ' (optional)'}</span>
                <textarea
                  className={styles.queryBox}
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  placeholder={standalone
                    ? (targetLang === 'por'
                        ? `e.g. "ter, ser and estar in the pretérito perfeito"\nor "the most common verbs in the present subjunctive"`
                        : targetLang === 'eng'
                          ? `e.g. "irregular verbs in the past simple"\nor "have and go in the present perfect"`
                          : `e.g. "the most common Russian verbs in present & past"\nor "ser and estar in the past tense in Spanish"`)
                    : 'Leave blank to drill this class’s verbs, or focus it — e.g. "just past tense", "focus on идти and хотеть"'}
                  rows={2}
                />
              </div>

              <div className={styles.field}>
                <span className={styles.fieldLabel}>Tense (pick one or more)</span>
                {groupTenses(tenseSet).map(g => {
                  const cols = Math.min(3, g.items.length)
                  return (
                    <div key={g.name || '_'} className={styles.tenseGroup}>
                      {g.name ? <div className={styles.tenseGroupLabel}>{g.name}</div> : null}
                      <div className={`${styles.tenseGrid} ${cols === 3 ? styles.tenseGridWide : ''}`} style={{ '--cols': String(cols) }}>
                        {g.items.map(t => (
                          <button key={t.key} className={`${styles.tenseChip} ${tenses.includes(t.key) ? styles.tenseChipActive : ''}`} onClick={() => toggleTense(t.key)}>{t.label}</button>
                        ))}
                      </div>
                    </div>
                  )
                })}
              </div>

              <div className={styles.field}>
                <span className={styles.fieldLabel}>Person</span>
                <select className={styles.select} value={person} onChange={e => setPerson(e.target.value)}>
                  {PERSONS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
                </select>
              </div>

              <div className={styles.field}>
                <span className={styles.fieldLabel}>How many per set</span>
                <div className={styles.chipRow}>
                  {[5, 10, 15].map(n => <button key={n} className={`${styles.chip} ${count === n ? styles.chipActive : ''}`} onClick={() => setCount(n)}>{n}</button>)}
                </div>
              </div>

              {phase === 'error' && <div className={styles.error}>{errorMsg}</div>}
              <button className={styles.generateBtn} onClick={() => generate(false)} disabled={!canStart}>🎤 Start drill</button>
            </>
          )}
        </div>
      </div>
    )
  }

  // ── render: drill ───────────────────────────────────────────────────────────
  const roman = romanizationOf(item?.sentence, targetLang)
  const showHint = attempts >= HINT_AFTER && item?.hint
  const recLangLabel = recognitionLangs[targetLang] || 'en-US'
  const got = states.length > 0 && states.every(Boolean)
  const showStem = attempts >= STEM_AFTER || revealed
  const revealCount = revealed ? (item?.ending || '').length : Math.max(0, Math.min((item?.ending || '').length, attempts - HINT_AFTER))
  const tenseOf = (k) => tenseSet.find(t => t.key === k)
  const itemTenseLabel = (() => { const t = tenseOf(item?.tense) || tenseSet.find(x => tenses.includes(x.key)); return t ? (t.group ? `${t.group} · ${t.label}` : t.label) : '' })()

  const Blank = () => {
    if (got) return <span className={styles.answerGot}>{item.conjugated}</span>
    if (!showStem) return <span className={styles.blankInline}>{'_'.repeat(Math.max(3, (item?.conjugated || '').length))}</span>
    const shownEnd = (item?.ending || '').slice(0, revealCount)
    const hiddenLen = Math.max(0, (item?.ending || '').length - revealCount)
    return (
      <span className={styles.answerInline}>
        <span className={styles.stem}>{item.stem}</span>
        <span className={styles.ending}>{shownEnd}</span>
        {hiddenLen > 0 && <span className={styles.blankInline}>{'_'.repeat(Math.max(1, hiddenLen))}</span>}
      </span>
    )
  }

  return (
    <div className={styles.view}>
      <button className={styles.closeBtn} onClick={() => setPhase('setup')}><CloseIcon /></button>
      {regenerating && <div className={styles.regenOverlay}><div className={styles.spinner} /><div className={styles.loadingText}>New set…</div></div>}

      <div className={styles.drill}>
        <div className={styles.drillTop}>
          <span className={styles.modeLabel}>Set {setNum} · {itemTenseLabel} · {languages[targetLang] || targetLang}</span>
          <div className={styles.comboWrap}><span className={styles.comboFire}>{combo >= 3 ? '🔥' : '⚡'}</span><span className={styles.combo} ref={comboRef}>{combo}</span></div>
        </div>

        <div className={styles.card} key={idx}>
          <div className={styles.subjectRow}>
            <span className={styles.subjectChip}>{item?.subjectEnglish || '—'}</span>
            {item?.subjectNative && <span className={styles.subjectNative}>{item.subjectNative}</span>}
            {item?.tense && <span className={styles.tenseTag}>{itemTenseLabel}</span>}
          </div>

          <div className={styles.verbInfo}>conjugate <strong>{item?.verb}</strong>{item?.meaning ? ` — ${item.meaning}` : ''}</div>

          {/* the sentence, with ONLY the verb blanked */}
          <div className={styles.sentenceLine}>
            <span className={styles.ctx}>{item?.prefix}</span>
            <Blank />
            <span className={styles.ctx}>{item?.suffix}</span>
          </div>

          {showRoman && roman && <div className={styles.roman}>{roman}</div>}
          {item?.english && <div className={styles.english}>{item.english}</div>}

          {showHint && <div className={styles.hintBox}>💡 {item.hint}</div>}
          {revealed && !got && <div className={styles.revealNote}>Answer: <strong>{item?.conjugated}</strong> — say it or skip ahead.</div>}
        </div>

        {answerMode === 'write' ? (
          <form className={styles.writeRow} onSubmit={(e) => { e.preventDefault(); submitTyped() }}>
            <input
              ref={typedRef}
              className={`${styles.writeInput} ${got ? styles.writeInputGot : ''}`}
              value={typed}
              onChange={e => setTyped(e.target.value)}
              placeholder={got ? item?.conjugated : 'type the conjugated verb…'}
              autoFocus
              disabled={got}
              spellCheck="false"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
            />
            <button type="submit" className={styles.writeBtn} disabled={!typed.trim() || got}>Check</button>
          </form>
        ) : null}

        <div className={styles.interim}>
          {answerMode === 'write'
            ? (got ? 'Right — next one coming up.' : attempts > 0 ? `${attempts} attempt${attempts === 1 ? '' : 's'} · accents optional, spelling isn’t` : 'type the conjugated verb and press enter')
            : micError ? micError : listening ? (interim || `mic open (${recLangLabel}) — say the verb`) : (attempts > 0 ? `${attempts} attempt${attempts === 1 ? '' : 's'}` : 'tap the mic and say the conjugated verb')}
        </div>

        <div className={styles.controls}>
          <button className={styles.sideBtn} onClick={speakConjugated} title="Hear the verb"><VolumeUpIcon /></button>
          {answerMode === 'write' ? (
            <button className={`${styles.micBtn} ${styles.checkBtn}`} onClick={submitTyped} disabled={!typed.trim() || got} title="Check the answer">
              <CheckIcon style={{ fontSize: 34 }} />
            </button>
          ) : (
            <button className={`${styles.micBtn} ${listening ? styles.micBtnActive : ''}`} onClick={() => (listening ? stopListening() : startListening())} title={listening ? 'Stop the mic' : 'Speak the conjugation'}>
              {listening ? <MicIcon style={{ fontSize: 34 }} /> : <MicOffIcon style={{ fontSize: 34 }} />}
            </button>
          )}
          <button className={styles.sideBtn} onClick={retry} title="Retry"><ReplayIcon /></button>
          <button className={styles.sideBtn} onClick={skip} title="Skip (resets streak)"><SkipNextIcon /></button>
        </div>

        <div className={styles.footRow}>
          <span className={styles.progress}>{idx + 1} / {items.length} · best {best}</span>
          {/* Answer by voice or by keyboard — the choice is remembered. */}
          <span className={styles.modeToggle}>
            <button className={`${styles.modeBtn} ${answerMode === 'speak' ? styles.modeBtnOn : ''}`} onClick={() => chooseAnswerMode('speak')} title="Answer out loud">
              <MicIcon style={{ fontSize: 14 }} /> speak
            </button>
            <button className={`${styles.modeBtn} ${answerMode === 'write' ? styles.modeBtnOn : ''}`} onClick={() => chooseAnswerMode('write')} title="Type the answer">
              <KeyboardIcon style={{ fontSize: 14 }} /> write
            </button>
          </span>
          <button className={styles.linkBtn} onClick={speakAnswer}>hear full sentence</button>
          {!revealed && <button className={styles.linkBtn} onClick={() => setRevealed(true)}>reveal answer</button>}
          <label className={styles.toggle}><input type="checkbox" checked={showRoman} onChange={e => setShowRoman(e.target.checked)} /> romanization</label>
        </div>
      </div>
    </div>
  )
}
